<template>
  <LoadingOverlay ref="loadingOverlay">
    <div
      id="productvariants"
      ref="tableContainer"
      class="position-relative overflow-hidden"
    >
      <div
        id="content-slider-indicator"
        class="c-comparisontable__indicator"
        :class="showIndicator ? '' : 'hidden'"
      >
        <img
          src="/assets/images/scroll-indicator.svg"
          width="35px"
          height="38px"
        />
      </div>

      <div class="c d-flex flex-wrap gap-10">
        <shopping-cart-notification
          v-for="{ message, severity } of baseShapePriceData.messages"
          :key="message"
          :variant="severity"
        >
          {{ message }}
        </shopping-cart-notification>
      </div>

      <div class="c table-responsive" @scroll="showIndicator = false">
        <table
          ref="table"
          class="c-product-variants-table table table-borderless mb-0"
        >
          <thead class="c-product-variants-table__head">
            <product-variants-table-head
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Type'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.segment"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Segments'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.safetyType"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Safety'
                ]
              "
              :tooltip-text="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.SafetyTooltip'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.weight"
              alignment="right"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Weight'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.priceEach"
              alignment="right"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.PriceEach'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.discount"
              alignment="right"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Discount'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.quantity"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Quantity'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.subtotal"
              alignment="right"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.Subtotal'
                ]
              "
            />

            <product-variants-table-head
              v-if="isVisible.deliveryDate"
              alignment="right"
              :label="
                getProductPageTranslations[
                  'ProductDetail.VariantsTable.Headings.DeliveryDate'
                ]
              "
            />
          </thead>

          <!-- Visible items -->
          <tbody>
            <product-variants-table-row
              v-for="product in visibleProducts"
              :key="product.productCode"
              :product="product"
            />
          </tbody>

          <!-- Initial hidden items -->
          <tbody v-if="showHiddenVariants && hiddenProducts.length">
            <product-variants-table-row
              v-for="product in hiddenProducts"
              :key="product.productCode"
              :product="product"
              :odd-even-class="visibleProductsEven"
            />
          </tbody>

          <!-- Teaser items -->
          <tbody v-if="!showHiddenVariants" class="position-relative">
            <product-variants-table-row
              v-for="product in teaserProducts"
              :key="product.productCode"
              :product="product"
              :odd-even-class="visibleProductsEven"
            />
            <product-variants-table-teaser
              v-if="hiddenProducts.length"
              :product-count="baseShapeData.products.length"
              @showHiddenVariants="showHiddenVariants = true"
            />
          </tbody>
        </table>
      </div>

      <ToastNotifications />
    </div>
  </LoadingOverlay>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import LoadingOverlay from '@modules/components/loading-overlay.vue';
import ProductVariantsTableHead from './product-variants-table-head.vue';
import ProductVariantsTableRow from './product-variants-table-row.vue';
import ProductVariantsTableTeaser from './product-variants-table-teaser.vue';
import ShoppingCartNotification from '../../shopping-cart/components/shopping-cart-notification.vue';

export default {
  name: 'ProductVariantsTable',
  components: {
    LoadingOverlay,
    ProductVariantsTableHead,
    ProductVariantsTableRow,
    ProductVariantsTableTeaser,
    ShoppingCartNotification
  },
  data() {
    return {
      showHiddenVariants: false,
      teaserItems: 2,
      showIndicator: false
    };
  },
  computed: {
    ...mapGetters('translations', ['getProductPageTranslations']),
    ...mapGetters('webshop', ['isVisible']),
    ...mapState(['culture', 'isAuthenticated']),
    ...mapState('webshop', ['baseShapeData', 'baseShapePriceData']),
    hiddenProducts() {
      return this.baseShapeData.products.filter(item => !item.directVisible);
    },
    teaserProducts() {
      return this.hiddenProducts.slice(0, this.teaserItems);
    },
    visibleProducts() {
      return this.baseShapeData.products.filter(item => item.directVisible);
    },
    visibleProductsEven() {
      return this.visibleProducts % 2 ? 'odd' : 'even';
    }
  },
  async created() {
    this.getTranslations({
      culture: this.culture,
      dictionaryKey: 'ProductDetail'
    });
  },
  async mounted() {
    if (this.$refs.table.clientWidth > this.$refs.tableContainer.clientWidth) {
      this.showIndicator = true;
    }

    if (this.isAuthenticated && this.baseShapeData.orderable) {
      await this.$refs.loadingOverlay.startLoading(async () => {
        await this.$filters.getFormats();
        await this.getBaseShapePriceData();
      });
    }
  },
  methods: {
    ...mapActions('webshop', ['getBaseShapePriceData']),
    ...mapActions('translations', ['getTranslations'])
  }
};
</script>

<style lang="scss" scoped>
@import '@/css/src/utilities/_variables.scss';
@import '~bootstrap/scss/mixins';

.c-product-variants-table {
  width: 100%;
  font-size: 15px;

  &__head {
    th {
      background-color: $grey-200;
      font-weight: $font-weight--semibold;
      padding: 20px;
      border-bottom: 1px solid $light;

      @include media-breakpoint-down(md) {
        padding: 15px;
      }
    }
  }
}
</style>
